import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { addReferral, checkOnlineStatus, ReferralData } from '@/services/firebase';
import { FaUserPlus } from 'react-icons/fa';
import { Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';

interface ReferralFormProps {
  onSuccess: () => void;
}

const ReferralForm: React.FC<ReferralFormProps> = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referrerCode, setReferrerCode] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    phone: '',
    notes: '',
    referrerCode: ''
  });
  
  useEffect(() => {
    const handleOnline = () => {
      console.log("Browser is online");
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      console.log("Browser is offline");
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    const handleConnectionChange = (event: CustomEvent) => {
      console.log("Connection status changed:", event.detail);
      setIsOnline(event.detail.online);
    };
    
    window.addEventListener('connection-status-change', handleConnectionChange as EventListener);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('connection-status-change', handleConnectionChange as EventListener);
    };
  }, []);
  
  useEffect(() => {
    const storedCode = localStorage.getItem('referrerCode');
    if (storedCode) {
      setReferrerCode(storedCode);
      setFormData(prev => ({ ...prev, referrerCode: storedCode }));
    } else {
      console.warn("No referrer code found in localStorage");
    }
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isOnline) {
      toast.error("You appear to be offline. Please check your internet connection before submitting.");
      return;
    }
    
    if (!formData.referrerCode) {
      toast.error("Missing referrer code. Please log in again.");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const referralToAdd: ReferralData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        phone: formData.phone.replace(/\D/g, ''),
        notes: formData.notes,
        referrerCode: formData.referrerCode,
        status: 'new'
      };
      
      console.log("Submitting referral with formatted data:", referralToAdd);
      
      const referralId = await addReferral(referralToAdd);
      
      if (!referralId) {
        throw new Error("Failed to add referral");
      }
      
      console.log("Referral added to Firebase with ID:", referralId);
      
      try {
        const WEBHOOK_URL = 'https://hook.us1.make.com/x9hvmwreuek2n2iv754ua6pe96k84mhe';
        
        const response = await axios.post(WEBHOOK_URL, {
          ...referralToAdd,
          referralId,
          submittedAt: new Date().toISOString()
        }, {
          timeout: 8000,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        console.log('Webhook notification sent successfully:', response.data);
      } catch (webhookError) {
        console.error(`Failed to send webhook notification:`, webhookError);
        toast.warning("Referral saved, but notification service couldn't be reached. The team will still receive your referral.");
      }
      
      setFormData({
        firstName: '',
        lastName: '',
        address: '',
        phone: '',
        notes: '',
        referrerCode
      });
      
      setOpen(false);
      onSuccess();
      toast.success("Referral added successfully");
    } catch (error) {
      console.error("Add referral error:", error);
      
      if (error.message && error.message.includes("offline")) {
        toast.error("Unable to add referral while offline. Please check your internet connection and try again.");
      } else {
        toast.error("Failed to add referral. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const NetworkStatus = () => (
    <div className={`flex items-center justify-center space-x-2 mb-2 ${isOnline ? 'text-green-500' : 'text-red-500'}`}>
      {isOnline ? (
        <>
          <Wifi className="h-4 w-4" />
          <span className="text-sm font-medium">Online</span>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Offline - Cannot Submit</span>
        </>
      )}
    </div>
  );
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="glass-button flex items-center space-x-2">
          <FaUserPlus className="mr-2" />
          <span>New Referral</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-panel sm:max-w-[500px] animate-fadeIn">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Add New Referral</DialogTitle>
          <DialogDescription className="text-center text-base">
            Share the gift of solar! Add your referral's details below
          </DialogDescription>
        </DialogHeader>
        
        <NetworkStatus />
        
        {!isOnline && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <p className="text-sm">You are offline. Please connect to the internet before submitting.</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                placeholder="John"
                required
                className="input-style"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                placeholder="Doe"
                required
                className="input-style"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              name="address"
              placeholder="123 Main St, City, State, Zip"
              required
              className="input-style"
              value={formData.address}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              placeholder="(123) 456-7890"
              required
              className="input-style"
              value={formData.phone}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              name="notes"
              placeholder="Additional information about this referral"
              className="input-style min-h-[80px]"
              value={formData.notes}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="referrerCode">Your Referrer Code</Label>
            <Input
              id="referrerCode"
              name="referrerCode"
              readOnly
              className="input-style bg-muted"
              value={formData.referrerCode}
            />
            {!formData.referrerCode && (
              <p className="text-red-500 text-xs">No referrer code found. Please log in again.</p>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
              className="border-accent text-accent hover:bg-accent/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="glass-button"
              disabled={isSubmitting || !isOnline}
            >
              {isSubmitting ? "Submitting..." : "Add Referral"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReferralForm;
